import { ApolloProvider } from '@apollo/react-common';
import ApolloClient from 'apollo-client';
import { PostHog } from 'posthog-js';
/* eslint-disable @typescript-eslint/ban-ts-comment */
// @ts-ignore
import { User } from 'shared/types/user';
import { useApolloClient, useQuery } from '@apollo/react-hooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { formatUserId } from 'components/PostHog/utils';
import { formatFloatNumber } from '@UIComponents/utils/formatters';
import UIButton from 'components/UI/Button/Button';
import { Cell, Row, Table, SortingState } from '@tanstack/react-table';
import { PosthogEvents } from 'components/PostHog/PosthogEvents';
import ClipboardCopyLabel from '@UIComponents/ClipboardCopyLabel/ClipboardCopyLabel';
import useDialog from 'components/UI/Dialog/useDialog';
import UIDialog from 'components/UI/Dialog/Dialog';
import { ViewershipReactTable } from './ViewershipReactTable';
import type { ExtendedViewership } from './types';
import { watchApiClient, GET_VIEWERSHIPS } from '../../../watchApi/watchApi';
import ViewershipDetails from '../ViewershipDetail/ViewershipDetail';
import ManualViewershipDialogForm from '../ManualViewership/DialogForm/DialogForm';
import { getStreamingNetworks } from '../utils';
import GET_MANUAL_VIEWERSHIPS_BY_BEN_SOURCE, {
  ViewershipType,
} from './graphql/getManualViewershipBySource';

import type {
  Viewership,
  ViewershipDetail,
  Station,
} from '../../../watchApi/types';
import { columnDefs as columnDefsFn } from './columnDefs';

const LABEL_SECONDARY = 'Secondary';
const PERIOD_NEXT_365 = '365 days';
const ALL_STATIONS = 'All Stations';
const COMBINED = 'Combined';
const CLICKABLECELL = 'clickable-cell';

const detailsMap: Record<string, keyof Viewership> = {
  premiereImpressions: 'premiereDetails',
  past30Impressions: 'past30Details',
  past60Impressions: 'past60Details',
  past90Impressions: 'past90Details',
  past365Impressions: 'past365Details',
};

type SelectedDetailsParams = {
  details: ViewershipDetail | null;
  impressions: number;
  airingIds: number;
  period: string;
  station: Station;
  watchStation: Station;
  provider: string;
  premiereDate: string;
  notes: string | null;
};

type WatchApiContentProps = {
  readonly watchSourceId: number;
  readonly benSourceId: number;
  readonly placementId?: number;
  readonly postHog: PostHog | null;
  readonly user: User | null;
  readonly benApolloClient: ApolloClient<object>;
};

export type InfoCellParams = {
  id: string;
  rowIndex: number;
  data: Viewership & {
    isSecondaryViewership: boolean;
    isPrimaryViewership: boolean;
    info: React.ReactNode;
  };
};

const getViewershipDescription = (data: Viewership) => {
  if (data?.provider?.toUpperCase() === 'MANUAL') {
    return `Manually entered viewership for ${
      data?.station?.displayName ?? ''
    }.`;
  }

  if (data?.provider?.toUpperCase() === 'LEGACY') {
    return 'Viewership created from legacy BEN data.';
  }

  return data?.provider
    ? `Viewership created from data partner ${data?.provider}.`
    : null;
};

const getViewershipInfoContents = (data: Viewership) => {
  const title = getViewershipDescription(data);

  // For streaming, we sometimes have the streaming networks in a JSON inside notes
  const streamingNetworks = getStreamingNetworks(data?.notes);

  return (
    <div>
      <p>
        <strong>{title}</strong>
      </p>
      {streamingNetworks && (
        <p>
          <strong>Streaming services: </strong>
          {streamingNetworks.join(', ')}.
        </p>
      )}
      {data?.notes && !streamingNetworks && (
        <>
          <p>
            <strong>Notes:</strong>
          </p>
          <p>{data?.notes}</p>
        </>
      )}
    </div>
  );
};

const getBottomRowData = (totals: {
  premiereImpressions: number;
  past30Impressions: number;
  past60Impressions: number;
  past90Impressions: number;
  past365Impressions: number;
}): ExtendedViewership[] => [
  {
    label: 'Total',
    isPinnedBottom: true,
    id: '',
    type: '',
    notes: '',
    premiereDate: '',
    provider: '',
    premiereImpressions: Number(totals.premiereImpressions) || 0,
    past30Impressions: Number(totals.past30Impressions) || 0,
    past60Impressions: Number(totals.past60Impressions) || 0,
    past90Impressions: Number(totals.past90Impressions) || 0,
    past365Impressions: Number(totals.past365Impressions) || 0,
    healthMetadata: [],
    station: {
      id: 0,
      displayName: '',
      country: '',
      image: '',
    },
    watchStation: {
      id: 0,
      displayName: '',
      country: '',
      image: '',
    },
    premiereDetails: null,
    past30Details: null,
    past60Details: null,
    past90Details: null,
    past365Details: null,
  },
];

const sendPostHogClickDetails = ({
  posthog,
  user,
  placementId,
  benSourceId,
  watchSourceId,
  stationName,
  period,
  provider,
  viewershipLabel,
}: {
  posthog: PostHog | null;
  user: User | null;
  placementId: number | undefined;
  benSourceId: number | undefined;
  watchSourceId: number | undefined;
  stationName: string;
  period: string;
  provider: string;
  viewershipLabel: string;
}) => {
  posthog?.capture('pp:viewershipdetails_click', {
    userId: user?.id && formatUserId(user?.id),
    placementId,
    benSourceId,
    watchSourceId,
    provider,
    stationName,
    viewershipLabel,
    period,
  });
};

// Add this new function to aggregate details for the total row
function aggregateViewershipDetails(
  viewerships: Viewership[],
): ViewershipDetail {
  const initialDetails: ViewershipDetail = {
    male: 0,
    female: 0,
    age_1_17: 0,
    age_18_24: 0,
    age_25_34: 0,
    age_35_44: 0,
    age_45_54: 0,
    age_55_65: 0,
    age_65_plus: 0,
    age_21_plus: 0,
    ethnicity_white: 0,
    ethnicity_hispanic: 0,
    ethnicity_black: 0,
    ethnicity_asian: 0,
    ethnicity_other: 0,
    income_under_20: 0,
    income_under_50: 0,
    income_20_49: 0,
    income_50_74: 0,
    income_75_99: 0,
    income_100_149: 0,
    income_150_199: 0,
    income_100_plus: 0,
    income_200_plus: 0,
    extraData: {},
    description: 'Aggregated viewership details for all stations',
    airingIds: [],
  };

  return viewerships.reduce((acc, viewership) => {
    const details = viewership.past365Details;
    Object.keys(acc).forEach((key) => {
      if (key !== 'description' && key !== 'extraData' && key !== 'airingIds') {
        const numericKey = key as keyof Omit<
          ViewershipDetail,
          'description' | 'extraData' | 'airingIds'
        >;
        if (details) {
          acc[numericKey] = (acc[numericKey] ?? 0) + (details[numericKey] ?? 0);
        }
      }
      if (key === 'airingIds') {
        acc.airingIds = [
          ...(acc?.airingIds ?? []),
          ...(details?.airingIds ?? []),
        ];
      }
    });
    return acc;
  }, initialDetails);
}

// Create a new component to handle the watch API specific queries
export function WatchApiProvider({
  children,
}: {
  readonly children: React.ReactNode;
}) {
  return <ApolloProvider client={watchApiClient}>{children}</ApolloProvider>;
}

function checkViewershipsWithProblems(props: {
  viewerships: Viewership[];
  watchId: number;
  sourceId?: number;
  posthog: PostHog | null;
  user: User | null;
  hasReportedProblems: React.MutableRefObject<boolean>;
}) {
  const {
    viewerships,
    watchId,
    sourceId,
    posthog,
    user,
    hasReportedProblems,
  } = props;

  const viewershipWithProblems = viewerships.filter(
    (viewership) =>
      viewership.healthMetadata.find(
        (metadata) => metadata.state === 'MISSING',
      ) !== undefined,
  );
  if (
    viewershipWithProblems.length > 0 &&
    !hasReportedProblems.current &&
    posthog &&
    user
  ) {
    // Mark that we've reported problems for this page view
    hasReportedProblems.current = true;

    // Send the PostHog event
    viewershipWithProblems.forEach((viewership) => {
      const data = {
        userId: user.id && formatUserId(user.id),
        watchId,
        sourceId,
        station: viewership.station.displayName,
        reportingWindows: viewership.healthMetadata
          .filter((meta) => meta.state === 'MISSING')
          .map((meta) => meta.reportingWindow),
        timestamp: Date.now(),
      };
      posthog.capture(PosthogEvents.MissingWatchData, data);
    });
  }
}

// eslint-disable-next-line max-lines-per-function, max-statements
export function WatchApiContent({
  watchSourceId,
  benSourceId,
  placementId,
  postHog,
  user,
  benApolloClient,
}: WatchApiContentProps) {
  const hasReportedProblems = useRef(false);
  const watchApolloClient = useApolloClient();

  const {
    data: viewershipsData,
    loading: mdeLoading,
    error: mdeError,
  } = useQuery(GET_VIEWERSHIPS, {
    variables: { sourceId: watchSourceId },
    client: watchApolloClient,
    skip: !watchSourceId,
  });

  const {
    data: manualViewershipsData,
    loading: manualLoading,
    error: manualError,
    refetch: refetchManualViewerships,
  } = useQuery(GET_MANUAL_VIEWERSHIPS_BY_BEN_SOURCE, {
    variables: {
      benSourceId,
      limit: 100,
      skip: 0,
      sort: 'createdAt:desc',
      type: ViewershipType.REPORTED,
    },
    client: benApolloClient,
    skip: !benSourceId,
  });

  const dialog = useDialog();
  const [manualViewershipId, setManualViewershipId] = useState<
    number | undefined
  >(undefined);

  const loading = mdeLoading ?? manualLoading;
  const error = mdeError ?? manualError;

  checkViewershipsWithProblems({
    watchId: watchSourceId,
    sourceId: benSourceId,
    viewerships: viewershipsData?.viewerships ?? [],
    posthog: postHog,
    user,
    hasReportedProblems,
  });

  const allViewerships = useMemo(
    () => [
      ...(viewershipsData?.viewerships ?? []),
      ...(manualViewershipsData?.manualViewershipsByBenSource ?? []),
    ],
    [viewershipsData, manualViewershipsData],
  );

  const [
    selectedDetails,
    setSelectedDetails,
  ] = useState<SelectedDetailsParams | null>(null);

  const [viewershipsWithLabels, setViewershipsWithLabels] = useState<
    ExtendedViewership[]
  >([]);

  const [sorting, setSorting] = useState<SortingState>([]);

  const getSortedViewerships = useCallback(
    (viewerships: typeof allViewerships) => {
      if (!viewerships) return [];
      return [...viewerships].sort((a, b) => {
        // Extract only the date part (year, month, day) without time
        const dateA = new Date(a.premiereDate);
        const dateB = new Date(b.premiereDate);

        const yearA = dateA.getFullYear();
        const monthA = dateA.getMonth();
        const dayA = dateA.getDate();

        const yearB = dateB.getFullYear();
        const monthB = dateB.getMonth();
        const dayB = dateB.getDate();

        // Compare year, month, day
        if (yearA !== yearB) return yearA - yearB;
        if (monthA !== monthB) return monthA - monthB;
        if (dayA !== dayB) return dayA - dayB;

        // If dates are on the same day, sort by past30Impressions in descending order
        return (b.past30Impressions ?? 0) - (a.past30Impressions ?? 0);
      });
    },
    [],
  );

  const createBaseData = useCallback(
    (sortedViewerships: typeof allViewerships) => {
      if (!sortedViewerships?.length) return [];
      return [
        {
          ...sortedViewerships[0],
          label: 'Primary',
          isPrimaryViewership: true,
          isSecondaryViewership: false,
          hasSecondaryLabel: false,
          isPinnedBottom: false,
          info: getViewershipInfoContents(sortedViewerships[0]),
        },
        {
          ...sortedViewerships[1],
          label: LABEL_SECONDARY,
          isSecondaryViewership: true,
          isPrimaryViewership: false,
          hasSecondaryLabel: true,
          isPinnedBottom: false,
          info: getViewershipInfoContents(sortedViewerships[1]),
        },
        ...sortedViewerships.slice(2).map((v) => ({
          ...v,
          isSecondaryViewership: true,
          isPrimaryViewership: false,
          isPinnedBottom: false,
          info: getViewershipInfoContents(v),
        })),
      ];
    },
    [],
  );

  const compareValues = useCallback(
    (aValue: unknown, bValue: unknown, sortDirection: number) => {
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return 1;
      if (bValue === undefined) return -1;
      if (aValue !== null && bValue !== null) {
        if (aValue < bValue) return -1 * sortDirection;
        if (aValue > bValue) return 1 * sortDirection;
      }
      return 0;
    },
    [],
  );

  const applySorting = useCallback(
    (baseData: ExtendedViewership[], currentSorting: typeof sorting) => {
      if (!currentSorting.length) return baseData;

      const primaryRow = baseData[0];
      const secondaryRows = baseData.slice(1);
      const sortColumn = currentSorting[0]?.id;

      if (!sortColumn) return baseData;

      const sortDirection = currentSorting[0]?.desc ? -1 : 1;
      const sortedSecondaryRows = [...secondaryRows].sort((a, b) => {
        const aValue = sortColumn in a ? a[sortColumn as keyof typeof a] : null;
        const bValue = sortColumn in b ? b[sortColumn as keyof typeof b] : null;
        return compareValues(aValue, bValue, sortDirection);
      });

      return [primaryRow, ...sortedSecondaryRows];
    },
    [compareValues],
  );

  useEffect(() => {
    if (!allViewerships) {
      setViewershipsWithLabels([]);
      return;
    }

    const sortedViewerships = getSortedViewerships(allViewerships);
    const baseData = createBaseData(sortedViewerships);
    const finalData = applySorting(baseData, sorting);
    setViewershipsWithLabels(finalData);
  }, [
    allViewerships,
    sorting,
    getSortedViewerships,
    createBaseData,
    applySorting,
  ]);

  const viewershipTotals = useMemo(
    () =>
      allViewerships.reduce(
        (totals, v) => ({
          premiereImpressions:
            totals.premiereImpressions + (v.premiereImpressions ?? 0),
          past30Impressions:
            totals.past30Impressions + (v.past30Impressions ?? 0),
          past60Impressions:
            totals.past60Impressions + (v.past60Impressions ?? 0),
          past90Impressions:
            totals.past90Impressions + (v.past90Impressions ?? 0),
          past365Impressions:
            totals.past365Impressions + (v.past365Impressions ?? 0),
        }),
        {
          premiereImpressions: 0,
          past30Impressions: 0,
          past60Impressions: 0,
          past90Impressions: 0,
          past365Impressions: 0,
        },
      ),
    [allViewerships],
  );

  // For backward compatibility
  const totalImpressions = viewershipTotals.past365Impressions;

  const totalAiringIds = useMemo(
    () =>
      allViewerships.reduce(
        (sum: number, v) => sum + (v.past365Details?.airingIds?.length ?? 0),
        0,
      ) ?? 0,
    [allViewerships],
  );

  useEffect(() => {
    if (!allViewerships?.length) return;

    const fakeStation = {
      id: 0,
      displayName: ALL_STATIONS,
      country: COMBINED,
      image: '',
    };

    if (totalImpressions > 0) {
      setSelectedDetails({
        details: aggregateViewershipDetails(allViewerships),
        impressions: totalImpressions,
        airingIds: totalAiringIds,
        period: PERIOD_NEXT_365,
        station: fakeStation,
        watchStation: fakeStation,
        provider: COMBINED,
        premiereDate: allViewerships[0]?.premiereDate ?? '',
        notes: '',
      });
    } else {
      // Get the first viewership's past30 data as default
      const rows = [...viewershipsWithLabels];

      // Find first row (Primary)
      const firstViewership = rows[0];

      if (firstViewership?.past30Impressions) {
        setSelectedDetails({
          details: firstViewership.past30Details,
          impressions: firstViewership.past30Impressions ?? 0,
          airingIds: firstViewership.past30Details?.airingIds?.length ?? 0,
          period: '30 days',
          station: firstViewership.station,
          watchStation: firstViewership.watchStation,
          provider: firstViewership.provider,
          premiereDate: firstViewership.premiereDate,
          notes: firstViewership.notes,
        });
      }
    }
  }, [allViewerships, totalImpressions, totalAiringIds, viewershipsWithLabels]);

  const onAddManualViewership = () => {
    postHog?.capture('pp:add_manual_viewership_click', {
      userId: user?.id && formatUserId(user?.id),
      placementId,
      sourceId: watchSourceId,
      watchSourceId,
      benSourceId,
    });

    dialog.open();
  };

  const onEditManualViewership = useCallback(
    (id: string) => {
      if (id) {
        postHog?.capture('pp:edit_manual_viewership_click', {
          userId: user?.id && formatUserId(user?.id),
          manualViewershipId: id.toString(),
          placementId,
          watchSourceId,
          benSourceId,
          viewershipId: id.toString(),
        });

        setManualViewershipId(parseInt(id, 10));
        dialog.open();
      }
    },
    [
      placementId,
      postHog,
      user,
      benSourceId,
      watchSourceId,
      dialog,
      setManualViewershipId,
    ],
  );

  const handleSortChange = useCallback((newSorting: SortingState) => {
    setSorting(newSorting);
  }, []);

  const selectCell = (cellElement: HTMLElement) => {
    // Remove 'cell-clicked' class from all cells
    document.querySelectorAll('.cell-clicked').forEach((el) => {
      el.classList.remove('cell-clicked');
    });

    // Add class to the clicked cell
    cellElement.classList.add('cell-clicked');
  };

  const handleTotalClick = () => {
    const fakeStation = {
      id: 0,
      displayName: ALL_STATIONS,
      country: COMBINED,
      image: '',
    };
    if (totalImpressions && totalImpressions > 0) {
      setSelectedDetails({
        details: aggregateViewershipDetails(viewershipsData?.viewerships ?? []),
        impressions: totalImpressions,
        airingIds: totalAiringIds,
        period: PERIOD_NEXT_365,
        station: fakeStation,
        watchStation: fakeStation,
        provider: COMBINED,
        premiereDate: viewershipsData?.viewerships?.[0]?.premiereDate ?? '',
        notes: '',
      });
    }

    sendPostHogClickDetails({
      posthog: postHog,
      user,
      placementId,
      benSourceId,
      watchSourceId,
      stationName: ALL_STATIONS,
      period: PERIOD_NEXT_365,
      provider: COMBINED,
      viewershipLabel: 'Total',
    });
  };

  const handleCellClicked = (
    event: React.MouseEvent<HTMLTableCellElement>,
    cell: Cell<ExtendedViewership, unknown>,
    row: Row<ExtendedViewership>,
  ) => {
    const cellElement = event.currentTarget as HTMLElement;
    if (
      !cellElement?.classList.contains(CLICKABLECELL) ||
      cell.getValue() === '--'
    ) {
      setSelectedDetails(null);
      return;
    }

    selectCell(cellElement);

    const { column } = cell;
    const columnId = column.id;
    // Handle clicks on any impression column in the bottom row
    if (
      row.original.isPinnedBottom &&
      (columnId === 'premiereImpressions' ||
        columnId === 'past30Impressions' ||
        columnId === 'past60Impressions' ||
        columnId === 'past90Impressions' ||
        columnId === 'past365Impressions')
    ) {
      handleTotalClick();
      return;
    }

    // Map column ID to details field name
    const detailsField = detailsMap[columnId];
    const columnHeader = column.columnDef.header as string;

    if (detailsField) {
      const details = row.original[detailsField] as ViewershipDetail;
      const impressions = Number(cell.getValue()) || 0;

      setSelectedDetails({
        details,
        impressions,
        airingIds: row.original.past365Details?.airingIds?.length ?? 0,
        period: columnHeader ?? '',
        station: row.original.station,
        watchStation: row.original.watchStation,
        provider: row.original.provider ?? '',
        premiereDate: row.original.premiereDate ?? '',
        notes: row.original.notes ?? '',
      });
    }

    sendPostHogClickDetails({
      posthog: postHog,
      user,
      placementId,
      benSourceId,
      watchSourceId,
      stationName: row.original.station?.displayName ?? '',
      period: columnHeader,
      provider: row.original.provider,
      viewershipLabel: row.original.label ?? '',
    });
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleGridReady = (_table: Table<ExtendedViewership>) => {
    // Wait for DOM to be fully painted, otherwise the cells are not rendered yet
    // and we select the wrong cell
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (totalImpressions > 0) {
          // Select the bottom row's 365-day impressions cell
          // Note: class is 'past365impressions' (no hyphen) due to kebab-case conversion
          const bottomCell = document.querySelector(
            '.bottom-row td.past365impressions.clickable-cell',
          ) as HTMLElement;

          if (bottomCell) {
            selectCell(bottomCell);
          }
          return;
        }

        // Find first row's past30 impressions cell (Primary viewership)
        const firstCell = document.querySelector(
          '.primary-row td.past30impressions.clickable-cell',
        ) as HTMLElement;
        if (firstCell) {
          selectCell(firstCell);
        }
      });
    });
  };

  const columnDefs = useMemo(() => columnDefsFn(onEditManualViewership), [
    onEditManualViewership,
  ]);

  const manualViewershipDialog = (
    <UIDialog
      isOpen={dialog.isOpen}
      onClose={dialog.close}
      title="Add Manual Viewership"
      description=""
      renderActions={() => (
        <ManualViewershipDialogForm
          manualViewershipId={manualViewershipId}
          placementId={placementId}
          benSourceId={benSourceId}
          watchSourceId={watchSourceId}
          onClose={() => {
            dialog.close();
            setManualViewershipId(undefined);
            refetchManualViewerships();
          }}
          benApolloClient={benApolloClient}
        />
      )}
    />
  );

  const addManualViewershipButton = (
    <UIButton
      onClick={() => onAddManualViewership()}
      theme="contained-purple"
      size="small"
      disabled={false}
      className="btn-add-manual-viewership"
    >
      + Add Manual Viewership
    </UIButton>
  );

  if (loading) return <div>Loading viewership data...</div>;

  if (error) {
    return (
      <div className="viewership-error">
        <div className="viewership-error-title">
          <span>❗Something went wrong while loading viewership data.</span>
        </div>
        <div className="viewership-error-message">
          You can try reloading the page. If the error persists, please contact
          support.
        </div>
        <pre className="viewership-error-message-details">
          - {error.message}
        </pre>
      </div>
    );
  }

  if (!allViewerships || allViewerships.length === 0) {
    return (
      <>
        {manualViewershipDialog}
        <div className="viewership-section">
          <div className="watch-audience-profile__title">
            <h3
              className="viewership-view__section__title"
              style={{ marginBottom: '35px' }}
            >
              Impressions
            </h3>
          </div>
          <div className="viewership-no-data">
            <span>Viewership data is not yet available</span>
          </div>
          {addManualViewershipButton}
        </div>
      </>
    );
  }

  return (
    <>
      {manualViewershipDialog}
      <div className="viewership-section">
        <div className="watch-audience-profile__title">
          <h3
            className="viewership-view__section__title"
            style={{ marginBottom: '35px' }}
          >
            {viewershipTotals?.past30Impressions ? (
              <>
                <ClipboardCopyLabel
                  label={`${formatFloatNumber(
                    viewershipTotals?.past30Impressions,
                  )}`}
                  copyValue={viewershipTotals?.past30Impressions.toString()}
                />{' '}
                Impressions in the first 30 days
              </>
            ) : (
              'Impressions'
            )}
          </h3>
        </div>

        <ViewershipReactTable
          data={viewershipsWithLabels.slice(1)}
          columnDefs={columnDefs}
          topRowData={[viewershipsWithLabels[0]]}
          bottomRowData={getBottomRowData(viewershipTotals)}
          onGridReady={handleGridReady}
          onSortingChange={handleSortChange}
          onCellClick={handleCellClicked}
        />
        {addManualViewershipButton}
        {selectedDetails && (
          <ViewershipDetails
            impressions={selectedDetails.impressions}
            viewershipDetail={selectedDetails.details}
            station={selectedDetails.station}
            watchStation={selectedDetails.watchStation}
            provider={selectedDetails.provider}
            premiere={selectedDetails.premiereDate}
            period={selectedDetails.period}
            notes={selectedDetails.notes ?? ''}
          />
        )}
      </div>
    </>
  );
}
